import {
  email as emailValidator,
  url as urlValidator,
  required as requiredValidator,
  maxValue as maxValueValidator,
  minValue as minValueValidator,
  maxLength as maxLengthValidator,
  minLength as minLengthValidator,
} from '@vuelidate/validators';
import { dynamicCacheKey } from 'dashboard/store/modules/wgptAutomationRegistry';

function containsVariable(value) {
  if (
    value === null ||
    value === undefined ||
    Array.isArray(value) ||
    typeof value === 'object' ||
    typeof value === 'number'
  )
    return false;
  return /%{.*}/.test(value);
}

function getFieldValueByPath(field, path) {
  const pathParts = path.split('.');
  let currentField = field;
  pathParts.forEach(pathPart => {
    currentField = currentField[pathPart];
  });
  return currentField;
}

function getUniqueFieldKeys(fields, prefix = null) {
  const fieldNames = [];
  fields.forEach(field => {
    if (field.type === 'group') {
      const childFieldNames = getUniqueFieldKeys(field.controls, field.name);
      fieldNames.push(
        ...childFieldNames.map(childFieldName =>
          prefix ? `${prefix}.${childFieldName}` : childFieldName
        )
      );
      return;
    }
    if (field.unique) fieldNames.push(field.name);
  });
  return fieldNames;
}

function getDuplicateUniqueKeys(uniqueKeys, value) {
  return uniqueKeys.filter(uniqueKey => {
    const values = value.map(valueItem =>
      getFieldValueByPath(valueItem, uniqueKey)
    );
    const filteredValues = values.filter(
      val => val !== null && val !== undefined
    );
    return filteredValues.length !== new Set(filteredValues).size;
  });
}

function validateRequired(field, value) {
  const valueToValidate = value ?? field.default;
  if (!field.required) return;
  if (field.type === 'checkbox') {
    if (valueToValidate === true) return;
  } else if (requiredValidator.$validator(valueToValidate)) return;
  throw new Error(`'${field.name}' is required, but it was ${valueToValidate}`);
}

function validateMin(field, value) {
  if (field.min == null) return;
  if (containsVariable(value)) return;
  if (['array', 'multiselect'].includes(field.type)) {
    if (minLengthValidator(field.min).$validator(value)) return;
    throw new Error(
      `'${field.name}' must have at least ${field.min} items, but it had ${value.length}`
    );
  } else if (field.type === 'number') {
    if (minValueValidator(field.min).$validator(value)) return;
    throw new Error(
      `'${field.name}' must be at least ${field.min}, but it was ${value}`
    );
  }
}

function validateMax(field, value) {
  if (field.max == null) return;
  if (containsVariable(value)) return;

  if (['array', 'multiselect'].includes(field.type)) {
    if (maxLengthValidator(field.max).$validator(value)) return;
    throw new Error(
      `'${field.name}' must have at most ${field.max} items, but it had ${value.length}`
    );
  } else if (field.type === 'number') {
    if (maxValueValidator(field.max).$validator(value)) return;
    throw new Error(
      `'${field.name}' must be at most ${field.max}, but it was ${value}`
    );
  }
}

function validateEmailField(field, value) {
  if (field.type !== 'email') return;
  if (containsVariable(value)) return;
  if (emailValidator.$validator(value)) return;
  throw new Error(`'${field.name}' must be a valid email, but it was ${value}`);
}

function validateUrlField(field, value) {
  if (field.type !== 'url') return;
  if (containsVariable(value)) return;
  if (urlValidator.$validator(value)) return;
  throw new Error(`'${field.name}' must be a valid URL, but it was ${value}`);
}

function validateArrayField(field, value, context) {
  if (field.type !== 'array') return;

  value = value || [];

  if (field.fixed_controls) {
    const fixedControl = {
      type: 'group',
      controls: field.fixed_controls.controls || [],
    };
    value.forEach(valueItem => {
      // eslint-disable-next-line no-use-before-define
      validateField(fixedControl, valueItem, context);
    });
    return;
  }

  const arrayFieldControl = field.controls[0];
  value.forEach(valueItem => {
    // eslint-disable-next-line no-use-before-define
    validateField(arrayFieldControl, valueItem, context);
  });
  const uniqueKeys = getUniqueFieldKeys(arrayFieldControl.controls);
  const duplicateUniqueKeys = getDuplicateUniqueKeys(uniqueKeys, value);
  if (duplicateUniqueKeys.length > 0) {
    throw new Error(
      `'${
        field.name
      }' contains duplicate values for unique keys: ${duplicateUniqueKeys.join(
        ', '
      )}`
    );
  }
}

function validateGroupField(field, value, context) {
  value = value || {};
  if (field.type === 'group') {
    const childContext = {
      ...context,
      paramValues: { ...(context?.paramValues || {}), ...value },
    };
    field.controls.forEach(fieldItem => {
      // eslint-disable-next-line no-use-before-define
      if (skipValidation(fieldItem, value, childContext)) return;
      // eslint-disable-next-line no-use-before-define
      validateField(fieldItem, value[fieldItem.name], childContext);
    });
  }
}

function resolveOptionsCacheKey(field, paramValues) {
  if (!field.params?.length) return field.model;
  const params = Object.fromEntries(
    field.params.map(p => [p, paramValues?.[p]])
  );
  if (field.params.some(p => !params[p] && params[p] !== false)) return null;
  return dynamicCacheKey(field.model, params);
}

function validateSelectOptionExists(field, value, context) {
  if (!['select', 'multiselect'].includes(field.type)) return;
  if (!field.model) return;
  if (containsVariable(value)) return;

  const { getDynamicOptions, getCustomObjects, paramValues } = context || {};

  const cacheKey = resolveOptionsCacheKey(field, paramValues);
  if (!cacheKey) return;

  let record = null;
  if (field.wrapper && field.custom_object && getCustomObjects) {
    record = getCustomObjects(field.custom_object);
  } else if (getDynamicOptions) {
    record = getDynamicOptions(cacheKey);
  }

  // Record not in store yet — skip to avoid false errors
  if (!record) return;

  const options = record.options || [];
  const values = Array.isArray(value) ? value : [value];
  values.forEach(val => {
    if (!val) return;
    const exists = options.some(
      opt => opt.value === val || opt.name === val || opt.id === val
    );
    if (!exists) {
      throw new Error(
        `'${field.name}': selected option '${val}' does not exist`
      );
    }
  });
}

function validateNestedVariableField(value, parts, wrapper, context) {
  if (parts.length === 0) return;
  const { getCustomObjects, getDynamicOptions } = context;

  let record = null;
  if (wrapper.custom_object && getCustomObjects) {
    record = getCustomObjects(wrapper.custom_object);
  } else if (wrapper.model && getDynamicOptions) {
    record = getDynamicOptions(wrapper.model);
  }
  // Record not in store yet — skip to avoid false errors
  if (!record) return;

  const options = record.options || [];
  const nestedField = options.find(
    opt => opt.name === parts[0] || opt.value === parts[0]
  );
  if (!nestedField) {
    throw new Error(`Variable '${value}' does not exist`);
  }

  // Custom objects can nest other custom objects (e.g. car_listing.make.name)
  if (parts.length > 1 && nestedField.wrapper) {
    validateNestedVariableField(value, parts.slice(1), nestedField, context);
  }
}

function validateVariableField(field, value, context) {
  if (field.type !== 'variable') return;
  const { variables } = context;
  if (!value || !variables || variables.length === 0) return;

  const hasDot = String(value).includes('.');
  if (!hasDot) {
    const exists = variables.some(v => !v.wrapper && v.name === value);
    if (!exists) {
      throw new Error(`Variable '${value}' does not exist`);
    }
    return;
  }

  const parts = String(value).split('.');
  const wrapper = variables.find(v => v.wrapper && v.name === parts[0]);
  if (!wrapper) {
    throw new Error(`Variable '${value}' does not exist`);
  }

  validateNestedVariableField(value, parts.slice(1), wrapper, context);
}

export function validateField(field, value, context) {
  validateRequired(field, value);
  if (value !== null && value !== undefined) {
    validateMin(field, value);
    validateMax(field, value);
    validateEmailField(field, value);
    validateUrlField(field, value);
    if (context) {
      validateVariableField(field, value, context);
      validateSelectOptionExists(field, value, context);
    }
  }
  validateArrayField(field, value, context);
  validateGroupField(field, value, context);
}

export function skipValidation(field, value, context = {}) {
  const conditions = field.show_if ?? [];
  const shown = conditions.every(condition => {
    if (condition.dynamic_flag) {
      const { getDynamicFlags } = context;
      if (!getDynamicFlags) return true;
      const flagName = condition.dynamic_flag;
      const paramFields = condition.params ?? [];
      const params = {};
      paramFields.forEach(p => {
        params[p] = value[p];
      });
      if (paramFields.some(p => !params[p] && params[p] !== false)) {
        return false;
      }
      const cacheKey = dynamicCacheKey(flagName, params);
      const record = getDynamicFlags(cacheKey);
      return record ? !!record.value : false;
    }
    const actualValue = value[condition.field];
    const compareValues = Array.isArray(condition.value)
      ? condition.value
      : [condition.value];
    return compareValues.some(compareValue => {
      if (compareValue.includes('*')) {
        if (compareValue === '*') return !!actualValue;
        return `${actualValue ?? ''}`.includes(compareValue.replace(/\*/g, ''));
      }
      return actualValue === compareValue;
    });
  });
  return !shown;
}
